import { node } from "~/util/dom";
import { CONFIG } from "~/config";
import { setCoordinates, setMapset } from "~/state/map/state";
import { store } from "./state/store";

interface IPromoData {
	fromDate: Date | Array<Date>;
	toDate: Date | Array<Date>;
	className?: string;
	img: {
		[key: string]: string;
	}
	link?: string;
	analyticsClick?: string;
	analyticsImpress?: () => void;
	onclick?: (event: MouseEvent) => void;
	targetBlank?: boolean;
	langs?: Array<string>;
}

const mapyComBlogURL = (lang: string) => {
	switch (lang) {
		case "de":
			return "https://blog.seznam.cz/de/?p=59448&preview=true&_thumbnail_id=59374";
		case "en":
			return "https://blog.seznam.cz/en/?p=59442&preview=true&_thumbnail_id=59374";
		case "it":
			return "https://blog.seznam.cz/it/?p=59450&preview=true&_thumbnail_id=59374";
		case "pl":
			return "https://blog.seznam.cz/pl/?p=59446&preview=true&_thumbnail_id=59374";
		case "sk":
			return "https://blog.seznam.cz/sk/?p=59444&preview=true&_thumbnail_id=59374";
		default:
			return "https://blog.seznam.cz/2025/11/rok-s-mapy-com-premium-splnili-jsme-slib-a-pridali-jeste-vic/";
	}
};

export default class PromoHP {
	_dom: {
		container: HTMLDivElement;
	};

	_curLang: string;

	_data: Array<IPromoData>;

	_selItem: IPromoData;

	constructor() {
		this._dom = {
			container: node("div", { id: "promohp", className: "noprint" }),
		};
		this._curLang = CONFIG.defaultLang;
		this._data = [{
			fromDate: new Date(2025, 0, 21),
			toDate: new Date(2025, 1, 28, 23, 59, 59),
			img: {
				"1x": `/img/promo/bezky_${this._curLang}.jpg`,
				"2x": `/img/promo/bezky_${this._curLang}@2x.jpg`,
			},
			className: "bezky-promo",
			link: "/zimni?x=15.2222580&y=50.7973890&z=12",
			onclick: event => {
				event.preventDefault();

				store.dispatch(setMapset({ mapset: "zimni" }));
				store.dispatch(setCoordinates({
					longitude: 15.2222580,
					latitude: 50.7973890,
					zoom: 12,
				}));
				Mapy.getComponent("history").save();
			},
			analyticsClick: "banner_bezky_25",
			langs: ["cs", "en", "pl", "de", "sk"],
		}, {
			fromDate: new Date(2025, 9, 17, 0, 0, 0),
			toDate: new Date(2025, 11, 2, 23, 59, 59),
			img: {
				"1x": `/img/promo/app_${this._curLang}.jpg`,
				"2x": `/img/promo/app_${this._curLang}@2x.jpg`,
			},
			className: "app-promo",
			link: this._curLang === "de" ? "https://play.google.com/store/apps/details?id=cz.seznam.mapy&hl=de" : "https://play.google.com/store/apps/details?id=cz.seznam.mapy&hl=pl",
			targetBlank: true,
			analyticsClick: "banner_app_25",
			langs: ["de", "pl"],
		}, {
			fromDate: new Date(2025, 11, 3, 0, 0, 0),
			toDate: new Date(2026, 11, 5, 23, 59, 59),
			img: {
				"1x": `/img/promo/vyroci_${this._curLang}.jpg`,
				"2x": `/img/promo/vyroci_${this._curLang}@2x.jpg`,
			},
			className: "anniversary-promo",
			link: mapyComBlogURL(this._curLang),
			targetBlank: true,
			analyticsClick: "banner_anniversary_25",
			langs: ["cs", "sk", "en", "de", "pl", "it"],
		}];
		this._selItem = null;

		this._build();
	}

	impress() {
		// musi byt timeout pro prvni zobrazeni
		setTimeout(() => {
			if (this._selItem?.analyticsImpress && Mapy.getComponent("search").isActive()) {
				this._selItem.analyticsImpress();
			}
		}, 0);
	}

	getContainer() {
		return this._dom.container;
	}

	_build() {
		let cur = Date.now();
		let findItem: IPromoData = null;

		if (Mapy.CUS.userweb.actualDate) {
			try {
				const parse = Mapy.CUS.userweb.actualDate.split(".");

				cur = new Date(parseInt(parse[2]), parseInt(parse[1])-1, parseInt(parse[0])).getTime();
			} catch (error) {
				// error
			}
		}

		// najdeme aktualni
		this._data.every(item => {
			let notFind = true;

			if (!Array.isArray(item.fromDate)) {
				item.fromDate = [item.fromDate];
			}

			if (!Array.isArray(item.toDate)) {
				item.toDate = [item.toDate];
			}

			if (item.fromDate.length !== item.toDate.length) { // pokud se nam neshoduje delka pole preskocime, spatne zadana data
				return notFind;
			}

			for (let index = 0; index < item.fromDate.length; index++) {
				const fromdate = item.fromDate[index].getTime();
				const todate = item.toDate[index].getTime();

				if (cur >= fromdate && cur <= todate && (!item.langs || item.langs.indexOf(this._curLang) !== -1)) {
					findItem = item;
					notFind = false;
					break;
				}
			}

			return notFind;
		});

		this._selItem = findItem;

		if (findItem) {
			this._createItem(this._selItem);
		} else {
			this._dom.container.classList.add("hide");
		}
	}

	_createItem(data: IPromoData) {
		const link = node("a", { className: "link-item" });

		if (data.className) {
			link.classList.add(data.className);
		}

		const img = new Image();

		link.appendChild(img);
		// posleme pred klikem
		if (data.analyticsClick) {
			link.addEventListener("pointerdown", () => {
				Mapy.getComponent("logger").log(data.analyticsClick);
			});
		}

		link.href = data.link || "#";
		img.srcset = Object.keys(data.img).map(key => `${data.img[key]} ${key}`).join(", ");
		img.src = data.img["1x"];
		img.crossOrigin = "anonymous";

		if (typeof data.onclick === "function") {
			link.addEventListener("click", data.onclick);
		}

		if (data.targetBlank) {
			link.setAttribute("target", "_blank");
		}

		this._dom.container.appendChild(link);

		return link;
	}
}
